import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';

import { RecommendedActivity } from './types';
import RecommendationCard from './RecommendationCard';
import { ACTIVITY_DETAILS_COLORS, ACTIVITY_DETAILS_RESPONSIVE, ACTIVITY_DETAILS_TYPOGRAPHY } from './constants';

interface RecommendationsSectionProps {
  recommendations: RecommendedActivity[];
  onViewActivity?: (id: string) => void;
}

/**
 * RecommendationsSection Component
 * 
 * "Parents who liked this activity also enjoyed" section with horizontal Swiper carousel.
 * 
 * Design specs:
 * - Container: flex column, gap 16px
 * - Responsive padding and typography
 * - Cards extend beyond for scroll peek
 * 
 * Title:
 * - Font: DM Sans Bold, responsive 18px→20px→22px
 * - Letter spacing: -0.72px
 * - Color: #1B2831
 * 
 * Carousel:
 * - Gap between cards: 16px
 * - Cards can scroll horizontally
 * - Free mode for smooth scrolling
 */
export default function RecommendationsSection({ 
  recommendations, 
  onViewActivity 
}: RecommendationsSectionProps) {

  if (recommendations.length === 0) {
    return null; // Don't render section if no recommendations
  }

  return (
    <div
      className="flex flex-col"
      style={{
        marginTop: '24px',
        marginBottom: '48px', // Bottom spacing before end of scroll
      }}
    >
      {/* Title */}
      <h3
        className={`font-bold ${ACTIVITY_DETAILS_TYPOGRAPHY.recommendationsTitle} ${ACTIVITY_DETAILS_RESPONSIVE.horizontalMargin}`}
        style={{
          fontFamily: 'DM Sans, sans-serif',
          letterSpacing: '-0.72px',
          color: ACTIVITY_DETAILS_COLORS.textPrimary,
          marginTop: 0,
          marginBottom: '16px',
          lineHeight: 1.2,
        }}
      >
        Parents who liked this activity also enjoyed
      </h3>

      {/* Swiper Carousel */}
      <Swiper
        modules={[FreeMode]}
        spaceBetween={16}
        slidesPerView="auto"
        freeMode={true}
        className={ACTIVITY_DETAILS_RESPONSIVE.horizontalPadding}
        style={{
          width: '100%',
        }}
      >
        {recommendations.map((activity) => (
          <SwiperSlide 
            key={activity.id}
            style={{ width: 'auto' }}
          >
            <RecommendationCard
              activity={activity}
              onViewActivity={onViewActivity}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
